/**
 * Authenticated fetch wrapper.
 * - Redirects to /login on 401 responses.
 * - Aborts after `opts.timeoutMs` (default 30s) to prevent hung connections.
 * Drop-in replacement for window.fetch — same signature, same return type.
 */
export async function apiFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
    opts?: { timeoutMs?: number },
): Promise<Response> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), opts?.timeoutMs ?? 30_000);

    try {
        const res = await fetch(input, {
            // `no-store` so the browser can't replay a cached GET response
            // after a same-session write — e.g. AI Map updates chains, then
            // the immediate refetch must see them, not a 200 from disk cache.
            cache: 'no-store',
            ...init,
            signal: init?.signal ?? controller.signal,
        });

        if (res.status === 401 && typeof window !== 'undefined') {
            // Clear the invalid session cookie before redirecting to prevent a loop
            // (middleware sees cookie → allows page, requireAuth rejects → 401 → redirect → repeat)
            document.cookie = '__session=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT';
            window.location.href = `/login?redirect=${encodeURIComponent(window.location.pathname)}`;
            throw new Error('Session expired');
        }

        return res;
    } finally {
        clearTimeout(timeout);
    }
}
